import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FIELD_TYPE } from '../types';
import type { ProjectField, FieldValue } from '../types';
import { resolveFieldType } from '../registry/field-registry';
import { validateTaskFieldValue } from '../schemas';
import { useTaskFieldValues } from '../hooks/useTaskFieldValues';
import { useUpsertTaskFieldValue } from '../hooks/useTaskFieldValueMutations';
import {
  InlineTextEditor,
  CheckboxEditor,
  DateEditor,
  SelectEditor,
  MultiSelectEditor,
  EmailEditor,
  UrlEditor,
  PhoneEditor,
  CurrencyEditor,
} from './FieldValueEditors';
import { FileFieldValueEditor } from './FileFieldValueEditor';
import { SignatureFieldValueEditor } from './SignatureFieldValueEditor';

interface FieldValueCellProps {
  /** UUID of the field definition. */
  fieldId: string;
  /** UUID of the task whose value is being displayed/edited. */
  taskId: string;
  /** Full field definition — needed for type, options, and name. */
  field: ProjectField;
  /**
   * Rendering mode.
   * - `list`: compact inline cell used in TaskRow columns.
   * - `dialog`: used inside TaskMetadataGrid with label visible.
   */
  mode?: 'list' | 'dialog';
  /**
   * Pre-fetched values for this task (from `useTaskFieldValuesBatch`, one
   * request for the whole visible list instead of one per cell). When
   * provided, the cell skips its own per-task fetch entirely; when omitted
   * (e.g. a subtask row outside the batched set), it falls back to fetching
   * just this task's values on its own.
   */
  preloadedValues?: FieldValue[];
}

/**
 * Renders and edits a single custom field value for a task.
 *
 * @remarks
 * - Without `preloadedValues`, calls `useTaskFieldValues(taskId)` — all cells for the same task share one cache entry.
 * - `FieldValue.value` is always a string regardless of field type.
 * - Users type is read-only until the member picker spec is defined.
 */
export function FieldValueCell({
  fieldId,
  taskId,
  field,
  mode = 'list',
  preloadedValues,
}: FieldValueCellProps) {
  const { t } = useTranslation('customFields');
  const { data: fetchedValues = [] } = useTaskFieldValues(taskId, {
    enabled: preloadedValues === undefined,
  });
  const values = preloadedValues ?? fetchedValues;
  const { mutate: upsert, isPending } = useUpsertTaskFieldValue(taskId);
  const [validationError, setValidationError] = useState<string | null>(null);

  const current = values.find((v) => v.fieldId === fieldId);
  const value = current?.value ?? null;

  const fieldType = resolveFieldType(field.fieldType);
  const options = field.options ?? [];

  /**
   * Validates client-side before hitting the backend (`validateTaskFieldValue` mirrors
   * the rules from `frontend-spec-custom-project-fields.md` §2). Returns `false` so the
   * calling editor keeps itself open instead of closing on a rejected value — any
   * backend-side error that still gets through is left to the global toast handler.
   */
  function save(next: string): boolean {
    if (fieldType === null) return false;
    const error = validateTaskFieldValue(fieldType, next, field.required);
    if (error) {
      setValidationError(error);
      return false;
    }
    setValidationError(null);
    upsert({ fieldId, payload: { value: next } });
    return true;
  }

  // Editors driven by InlineTextEditor already show `validationError` inline while
  // editing (see FieldValueEditors.tsx) — no need to repeat it below the cell.
  const showsOwnError =
    fieldType === FIELD_TYPE.Text ||
    fieldType === FIELD_TYPE.Number ||
    fieldType === FIELD_TYPE.Currency ||
    fieldType === FIELD_TYPE.Email ||
    fieldType === FIELD_TYPE.Url ||
    fieldType === FIELD_TYPE.Phone ||
    fieldType === null;

  let editor: ReactNode;
  let wrapperClassName = 'flex items-center';

  switch (fieldType) {
    case FIELD_TYPE.Checkbox:
      editor = <CheckboxEditor value={value} isPending={isPending} onSave={save} />;
      break;
    case FIELD_TYPE.Select:
      editor = <SelectEditor value={value} options={options} isPending={isPending} onSave={save} />;
      break;
    case FIELD_TYPE.MultiSelect:
      wrapperClassName = 'flex flex-wrap items-center gap-1';
      editor = (
        <MultiSelectEditor value={value} options={options} isPending={isPending} onSave={save} />
      );
      break;
    case FIELD_TYPE.Date:
      editor = <DateEditor value={value} isPending={isPending} onSave={save} />;
      break;
    case FIELD_TYPE.Number:
      editor = <InlineTextEditor value={value} type="number" isPending={isPending} onSave={save} />;
      break;
    case FIELD_TYPE.Currency:
      editor = (
        <CurrencyEditor value={value} isPending={isPending} error={validationError} onSave={save} />
      );
      break;
    case FIELD_TYPE.Email:
      editor = (
        <EmailEditor value={value} isPending={isPending} error={validationError} onSave={save} />
      );
      break;
    case FIELD_TYPE.Url:
      editor = (
        <UrlEditor value={value} isPending={isPending} error={validationError} onSave={save} />
      );
      break;
    case FIELD_TYPE.Phone:
      editor = (
        <PhoneEditor value={value} isPending={isPending} error={validationError} onSave={save} />
      );
      break;
    case FIELD_TYPE.Files:
      editor = <FileFieldValueEditor value={value} fieldId={fieldId} taskId={taskId} />;
      break;
    case FIELD_TYPE.Signature:
      editor = <SignatureFieldValueEditor value={value} fieldId={fieldId} taskId={taskId} />;
      break;
    case FIELD_TYPE.Users:
      editor = (
        <span className="text-sm text-muted-foreground">{t('fieldValue.usersPendingSpec')}</span>
      );
      break;
    default:
      editor = <InlineTextEditor value={value} type="text" isPending={isPending} onSave={save} />;
  }

  return (
    <div
      data-testid={`custom-fields-value-cell-${fieldId}`}
      className="flex flex-col gap-0.5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={wrapperClassName}>
        {mode === 'dialog' && (
          <span className="mr-2 text-xs text-muted-foreground">{field.name}:</span>
        )}
        {editor}
      </div>
      {!showsOwnError && validationError && (
        <span
          className="text-xs text-destructive"
          data-testid={`custom-fields-value-validation-${fieldId}`}
        >
          {t(validationError)}
        </span>
      )}
    </div>
  );
}
